<!-- 重点供应商到货跟踪 -->
<template>
  <div class="key-supplier-delivery">
    <!-- 页面标题 + 操作按钮 -->
    <div class="page-header">
      <h2 class="page-title">重点供应商到货跟踪</h2>
      <div class="header-actions">
        <el-button type="primary" @click="handleExport">
          <i class="el-icon-download"></i> 导出报表
        </el-button>
        <el-button type="primary" @click="handleBatchReminder">
          <i class="el-icon-bell"></i> 发送提醒
        </el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <div class="filter-item">
        <label class="filter-label">时间范围</label>
        <el-select v-model="queryParams.timeRangeType" placeholder="请选择" style="width: 120px;" @change="handleTimeRangeChange">
          <el-option label="本季度" :value="1"></el-option>
          <el-option label="本月" :value="2"></el-option>
          <el-option label="本周" :value="3"></el-option>
          <el-option label="自定义" :value="4"></el-option>
        </el-select>
        <el-date-picker
          v-show="queryParams.timeRangeType === 4"
          v-model="customTimeRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="margin-left: 12px; width: 240px;"
        ></el-date-picker>
      </div>

      <div class="filter-item">
        <label class="filter-label">供应商</label>
        <el-select
          v-model="queryParams.custName"
          placeholder="请选择"
          filterable
          remote
          clearable
          reserve-keyword
          :remote-method="loadSupplierOptions"
          :loading="supplierLoading"
          style="width: 160px;"
          @change="handleSearch"
        >
          <el-option label="全部供应商" value=""></el-option>
          <el-option v-for="item in supplierOptions" :key="item.id" :label="item.deptName" :value="item.deptName"></el-option>
        </el-select>
      </div>

      <div class="filter-item">
        <label class="filter-label">品种</label>
        <el-select
          v-model="queryParams.cargoNo"
          placeholder="请选择"
          filterable
          remote
          clearable
          reserve-keyword
          :remote-method="loadCargoOptions"
          :loading="cargoLoading"
          style="width: 140px;"
          @change="handleSearch"
        >
          <el-option label="全部品种" value=""></el-option>
          <el-option v-for="item in cargoOptions" :key="item.varNo" :label="item.varName" :value="item.varNo"></el-option>
        </el-select>
      </div>

      <div class="filter-item">
        <label class="filter-label">异常状态</label>
        <div class="status-buttons">
          <button
            class="status-btn"
            :class="{ active: queryParams.exceptionStatus === null }"
            @click="queryParams.exceptionStatus = null; handleSearch()"
          >全部</button>
          <button
            class="status-btn"
            :class="{ active: queryParams.exceptionStatus === 0 }"
            @click="queryParams.exceptionStatus = 0; handleSearch()"
          >正常</button>
          <button
            class="status-btn"
            :class="{ active: queryParams.exceptionStatus === 1 }"
            @click="queryParams.exceptionStatus = 1; handleSearch()"
          >异常</button>
        </div>
      </div>

      <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
    </div>

    <!-- 核心指标卡片 -->
    <el-row :gutter="16" class="kpi-section">
      <el-col :span="6">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-label">平均到货率</span>
            <i class="el-icon-s-data kpi-icon"></i>
          </div>
          <div class="kpi-value">{{ statisticsData.averageFulfillmentRate || 0 }}<span class="kpi-unit">%</span></div>
          <div class="kpi-trend" :class="getTrendClass(statisticsData.averageFulfillmentRateTrend)">
            <i :class="getTrendIcon(statisticsData.averageFulfillmentRateTrend)"></i>
            {{ Math.abs(statisticsData.averageFulfillmentRateChange || 0) }}% 环比上月
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-label">供应商到货批次</span>
            <i class="el-icon-warning kpi-icon kpi-icon--warning"></i>
          </div>
          <div class="kpi-value">{{ statisticsData.exceptionBatchCount || 0 }}<span class="kpi-unit">批</span></div>
          <div class="kpi-trend" :class="getTrendClass(statisticsData.exceptionBatchCountTrend === -1 ? 1 : -1)">
            <i :class="getTrendIcon(statisticsData.exceptionBatchCountTrend === -1 ? 1 : -1)"></i>
            {{ Math.abs(statisticsData.exceptionBatchCountChange || 0) }}批 环比上月
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-label">到货合格率</span>
            <i class="el-icon-s-data kpi-icon"></i>
          </div>
          <div class="kpi-value">{{ statisticsData.qualityPassRate || 0 }}<span class="kpi-unit">%</span></div>
          <div class="kpi-trend" :class="getTrendClass(statisticsData.qualityPassRateTrend)">
            <i :class="getTrendIcon(statisticsData.qualityPassRateTrend)"></i>
            {{ Math.abs(statisticsData.qualityPassRateChange || 0) }}% 环比上月
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-label">合同完成进度</span>
            <i class="el-icon-s-data kpi-icon"></i>
          </div>
          <div class="kpi-value">{{ statisticsData.contractCompletionRate || 0 }}<span class="kpi-unit">%</span></div>
          <div class="kpi-trend" :class="getTrendClass(statisticsData.contractCompletionRateTrend, true)">
            <i :class="getTrendIcon(statisticsData.contractCompletionRateTrend, true)"></i>
            {{ Math.abs(statisticsData.contractCompletionRateChange || 0) }}% 环比上月
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="16" class="chart-section">
      <el-col :span="16">
        <el-card class="chart-card">
          <div slot="header" class="card-header">
            <span class="card-title">各供应商履约率对比</span>
          </div>
          <div id="fulfillmentChart" style="height: 350px;"></div>
          <!-- 图例 -->
          <div class="chart-legend">
            <div class="legend-item">
              <span class="legend-dot legend-dot--green"></span>
              <span>≥95%</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--orange"></span>
              <span>85%-95%</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--red"></span>
              <span>≤85%</span>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card class="chart-card">
          <div slot="header" class="card-header">
            <span class="card-title">异常事件类型分布</span>
          </div>
          <div id="exceptionChart" style="height: 350px;"></div>
          <!-- 图例 -->
          <div class="chart-legend chart-legend--vertical">
            <div class="legend-item">
              <span class="legend-dot legend-dot--blue"></span>
              <span>延期</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--orange"></span>
              <span>质量缺陷</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--red"></span>
              <span>规格不符</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--purple"></span>
              <span>其他</span>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 到货明细数据表格 -->
    <el-card class="table-card">
      <div slot="header" class="card-header">
        <span class="card-title">到货明细数据</span>
        <div class="table-actions">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索供应商..."
            prefix-icon="el-icon-search"
            clearable
            maxlength="50"
            size="small"
            style="width: 200px;"
            @input="handleTableSearch"
          ></el-input>
          <el-button size="small" @click="handleReset">重置</el-button>
        </div>
      </div>

      <el-table
        :data="paginatedSupplierList"
        style="width: 100%"
        v-loading="tableLoading"
        :header-cell-style="{ background: '#F9FAFB', color: '#6B7280', fontWeight: '500' }"
      >
        <el-table-column label="供应商名称" min-width="180">
          <template slot-scope="scope">
            <div class="supplier-info">
              <div class="supplier-name">{{ scope.row.custName }}</div>
              <div class="supplier-contact">联系人：{{ scope.row.contactPerson || '-' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="合同到货量(吨)" prop="totalContractWeight" width="130" align="right">
          <template slot-scope="scope">
            {{ formatNumber(scope.row.totalContractWeight) }}
          </template>
        </el-table-column>
        <el-table-column label="实际到货量(吨)" prop="totalInWeight" width="130" align="right">
          <template slot-scope="scope">
            {{ formatNumber(scope.row.totalInWeight) }}
          </template>
        </el-table-column>
        <el-table-column label="到货率" prop="fulfillmentRate" width="100" align="center">
          <template slot-scope="scope">
            <span class="rate-value" :class="getRateClass(scope.row.fulfillmentRate)">{{ scope.row.fulfillmentRate }}%</span>
          </template>
        </el-table-column>
        <el-table-column label="异常状态" width="140" align="center">
          <template slot-scope="scope">
            <div class="status-cell">
              <template v-if="scope.row.exceptionCount === 0">
                <i class="el-icon-success status-icon status-icon--success"></i>
                <span class="status-text status-text--success">正常</span>
              </template>
              <template v-else-if="scope.row.delayDays > 0 && scope.row.delayDays <= 3">
                <i class="el-icon-warning status-icon status-icon--warning"></i>
                <span class="status-text status-text--warning">延迟{{ scope.row.delayDays }}天</span>
              </template>
              <template v-else>
                <i class="el-icon-error status-icon status-icon--error"></i>
                <span class="status-text status-text--error">严重延误</span>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="执货周期" prop="deliveryPeriod" width="120" align="center">
          <template slot-scope="scope">
            {{ scope.row.deliveryPeriod || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="延误天数" prop="delayDays" width="100" align="center">
          <template slot-scope="scope">
            <span :class="{ 'delay-text--error': scope.row.delayDays > 0 }">{{ scope.row.delayDays || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="综合评分" width="140" align="center">
          <template slot-scope="scope">
            <el-rate
              v-model="scope.row.starLevel"
              disabled
              :colors="['#F59E0B', '#F59E0B', '#F59E0B']"
            ></el-rate>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" class="detail-btn" @click="handleViewDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <span class="total-text">显示 {{ startIndex }} 到 {{ endIndex }} 条，共 {{ filteredSupplierList.length }} 条记录</span>
        <el-pagination
          background
          layout="prev, pager, next"
          :total="filteredSupplierList.length"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getStatistics, sendReminder, exportReport, fetchSupplierOptions, fetchCargoOptions } from './api'
import { getCurrentMonthFirst, getCurrentMonthEnd } from '@/util/date.js'

const getCurrentQuarterFirst = () => {
  const now = new Date()
  const quarter = Math.floor(now.getMonth() / 3)
  const year = now.getFullYear()
  const month = quarter * 3 + 1
  return `${year}-${String(month).padStart(2, '0')}-01`
}

const getCurrentQuarterEnd = () => {
  const now = new Date()
  const quarter = Math.floor(now.getMonth() / 3)
  const year = now.getFullYear()
  const endMonth = quarter * 3 + 3
  const lastDay = new Date(year, endMonth, 0).getDate()
  return `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

const getCurrentWeekFirst = () => {
  const now = new Date()
  const day = now.getDay() || 7
  now.setDate(now.getDate() - day + 1)
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

const getCurrentWeekEnd = () => {
  const now = new Date()
  const day = now.getDay() || 7
  now.setDate(now.getDate() - day + 7)
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export default {
  name: 'key-supplier-delivery',
  data() {
    return {
      queryParams: {
        timeRangeType: 1,
        startTime: null,
        endTime: null,
        custName: '',
        cargoNo: '',
        exceptionStatus: null
      },
      customTimeRange: [],
      statisticsData: {},
      supplierFulfillmentList: [],
      exceptionDistributionList: [],
      tableLoading: false,
      searchKeyword: '',
      currentPage: 1,
      pageSize: 5,
      supplierOptions: [],
      cargoOptions: [],
      supplierLoading: false,
      cargoLoading: false
    }
  },
  computed: {
    filteredSupplierList() {
      let list = this.supplierFulfillmentList
      if (this.searchKeyword) {
        list = list.filter(item => item.custName && item.custName.includes(this.searchKeyword))
      }
      if (this.queryParams.exceptionStatus !== null) {
        if (this.queryParams.exceptionStatus === 0) {
          list = list.filter(item => !item.exceptionCount || item.exceptionCount === 0)
        } else {
          list = list.filter(item => item.exceptionCount > 0)
        }
      }
      return list
    },
    paginatedSupplierList() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredSupplierList.slice(start, end)
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    endIndex() {
      const end = this.currentPage * this.pageSize
      return Math.min(end, this.filteredSupplierList.length)
    }
  },
  mounted() {
    this.initTimeRange()
    this.loadFilterOptions()
    this.loadStatistics()
  },
  methods: {
    normalizeArray(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      if (data && data.data && Array.isArray(data.data.records)) return data.data.records
      if (data && Array.isArray(data.records)) return data.records
      return []
    },

    loadFilterOptions() {
      this.loadSupplierOptions('')
      this.loadCargoOptions('')
    },

    async loadSupplierOptions(keyword) {
      this.supplierLoading = true
      try {
        const params = keyword ? { deptName: keyword } : {}
        const res = await fetchSupplierOptions(params)
        this.supplierOptions = this.normalizeArray(res.data).map(item => ({
          id: item.id || item.deptId || item.value,
          deptName: item.deptName || item.name || item.label
        })).filter(item => item.id && item.deptName)
      } catch (e) {
        console.warn(e)
        this.supplierOptions = []
      } finally {
        this.supplierLoading = false
      }
    },

    async loadCargoOptions(keyword) {
      this.cargoLoading = true
      try {
        const params = { page: 1, size: 50 }
        if (keyword) params.varName = keyword
        const res = await fetchCargoOptions(params)
        this.cargoOptions = this.normalizeArray(res.data).map(item => ({
          varNo: item.varNo || item.value,
          varName: item.varName || item.cargoName || item.name || item.label
        })).filter(item => item.varNo && item.varName)
      } catch (e) {
        console.warn(e)
        this.cargoOptions = []
      } finally {
        this.cargoLoading = false
      }
    },

    initTimeRange() {
      if (this.queryParams.timeRangeType === 1) {
        this.queryParams.startTime = getCurrentQuarterFirst()
        this.queryParams.endTime = getCurrentQuarterEnd()
      } else if (this.queryParams.timeRangeType === 2) {
        this.queryParams.startTime = getCurrentMonthFirst()
        this.queryParams.endTime = getCurrentMonthEnd()
      } else if (this.queryParams.timeRangeType === 3) {
        this.queryParams.startTime = getCurrentWeekFirst()
        this.queryParams.endTime = getCurrentWeekEnd()
      }
    },

    handleTimeRangeChange() {
      if (this.queryParams.timeRangeType !== 4) {
        this.initTimeRange()
      }
    },

    async handleSearch() {
      if (this.queryParams.timeRangeType === 4) {
        if (!this.customTimeRange || this.customTimeRange.length !== 2) {
          this.$message.warning('请选择自定义时间范围')
          return
        }
        this.queryParams.startTime = this.customTimeRange[0]
        this.queryParams.endTime = this.customTimeRange[1]
      }
      this.loadStatistics()
    },

    handleReset() {
      this.queryParams = {
        timeRangeType: 1,
        startTime: null,
        endTime: null,
        custName: '',
        cargoNo: '',
        exceptionStatus: null
      }
      this.customTimeRange = []
      this.searchKeyword = ''
      this.currentPage = 1
      this.initTimeRange()
      this.loadStatistics()
    },

    async loadStatistics() {
      this.tableLoading = true
      try {
        const params = {
          timeRangeType: this.queryParams.timeRangeType,
          startTime: this.queryParams.startTime || '',
          endTime: this.queryParams.endTime || ''
        }
        if (this.queryParams.custName) params.custName = this.queryParams.custName
        if (this.queryParams.cargoNo) params.cargoNo = this.queryParams.cargoNo
        if (this.queryParams.exceptionStatus != null) params.exceptionStatus = this.queryParams.exceptionStatus

        const res = await getStatistics(params)

        if (res.data && res.data.success !== false) {
          this.statisticsData = res.data.data || {}
          this.supplierFulfillmentList = (res.data.data && res.data.data.supplierFulfillmentList) || []
          this.exceptionDistributionList = (res.data.data && res.data.data.exceptionTypeDistributionList) || []

          this.$nextTick(() => {
            this.renderFulfillmentChart()
            this.renderExceptionChart()
          })
        } else {
          this.$message.error((res.data && res.data.msg) || '获取统计数据失败')
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
        this.$message.error('获取统计数据失败')
      } finally {
        this.tableLoading = false
      }
    },

    // 渲染供应商履约率对比图（横向条形图）
    renderFulfillmentChart() {
      const chartDom = document.getElementById('fulfillmentChart')
      if (!chartDom) return

      const myChart = echarts.init(chartDom)
      const data = this.supplierFulfillmentList.slice(0, 10).map(item => ({
        name: item.custName || '',
        value: item.fulfillmentRate || 0
      }))

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: '{b}: {c}%'
        },
        grid: {
          left: '3%',
          right: '10%',
          bottom: '3%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          max: 100,
          axisLabel: { formatter: '{value}%' }
        },
        yAxis: {
          type: 'category',
          data: data.map(d => d.name),
          axisLabel: {
            width: 120,
            overflow: 'truncate',
            ellipsis: '...'
          }
        },
        series: [{
          type: 'bar',
          data: data.map(d => ({
            value: d.value,
            itemStyle: {
              color: this.getBarColor(d.value)
            }
          })),
          barWidth: '40%',
          label: {
            show: true,
            position: 'right',
            formatter: '{c}%',
            color: '#374151'
          }
        }]
      }

      myChart.setOption(option)
    },

    // 获取条形图颜色
    getBarColor(value) {
      if (value >= 95) return '#166534'  // 深绿色
      if (value >= 85) return '#F59E0B'  // 橙色
      return '#EF4444'  // 红色
    },

    // 渲染异常事件类型分布图（环形饼图）
    renderExceptionChart() {
      const chartDom = document.getElementById('exceptionChart')
      if (!chartDom) return

      const myChart = echarts.init(chartDom)

      // 模拟数据 - 延期、质量缺陷、规格不符、其他
      const data = [
        { name: '延期', value: 35, color: '#3B82F6' },
        { name: '质量缺陷', value: 20, color: '#F59E0B' },
        { name: '规格不符', value: 15, color: '#EF4444' },
        { name: '其他', value: 30, color: '#8B5CF6' }
      ]

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}次 ({d}%)'
        },
        series: [{
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['40%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          labelLine: { show: false },
          data: data.map(d => ({
            name: d.name,
            value: d.value,
            itemStyle: { color: d.color }
          }))
        }]
      }

      myChart.setOption(option)
    },

    handleTableSearch() {
      this.currentPage = 1
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    handleBatchReminder() {
      this.$message.success('提醒发送成功')
    },

    async handleExport() {
      const res = await exportReport(this.queryParams)
      if (res.data && res.data.success !== false) {
        this.$message.success('导出成功')
      }
    },

    handleViewDetail(row) {
      this.$message.info(`查看 ${row.custName} 详情`)
    },

    getTrendClass(trend, inverse = false) {
      if (trend === null || trend === 0) return ''
      if (inverse) {
        return trend === 1 ? 'trend-down' : 'trend-up'
      }
      return trend === 1 ? 'trend-up' : 'trend-down'
    },

    getTrendIcon(trend, inverse = false) {
      if (trend === null || trend === 0) return 'el-icon-minus'
      if (inverse) {
        return trend === 1 ? 'el-icon-bottom' : 'el-icon-top'
      }
      return trend === 1 ? 'el-icon-top' : 'el-icon-bottom'
    },

    getRateClass(rate) {
      if (rate >= 95) return 'rate-excellent'
      if (rate >= 85) return 'rate-good'
      return 'rate-poor'
    },

    formatNumber(num) {
      if (!num) return '0'
      return Number(num).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.key-supplier-delivery {
  padding: 20px;
  background: #F3F4F6;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .header-actions {
    display: flex;
    gap: 12px;

    .el-button {
      i {
        margin-right: 6px;
      }
    }
  }
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .filter-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-label {
    font-size: 14px;
    color: #6B7280;
    white-space: nowrap;
  }

  .status-buttons {
    display: inline-flex;

    .status-btn {
      padding: 8px 16px;
      font-size: 14px;
      color: #606266;
      background: #fff;
      border: 1px solid #DCDFE6;
      cursor: pointer;
      outline: none;
      transition: all 0.2s;

      &:first-child {
        border-radius: 4px 0 0 4px;
      }

      &:last-child {
        border-radius: 0 4px 4px 0;
      }

      &:not(:last-child) {
        border-right: none;
      }

      &:hover {
        color: #3B82F6;
      }

      &.active {
        background: #3B82F6;
        color: #fff;
        border-color: #3B82F6;
      }
    }
  }
}

.kpi-section {
  margin-bottom: 16px;
}

.kpi-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #E5E7EB;

  .kpi-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .kpi-label {
    font-size: 14px;
    color: #6B7280;
  }

  .kpi-icon {
    font-size: 18px;
    color: #9CA3AF;

    &--warning {
      color: #F59E0B;
    }
  }

  .kpi-value {
    font-size: 32px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 8px;
  }

  .kpi-unit {
    font-size: 16px;
    font-weight: 400;
    color: #6B7280;
    margin-left: 4px;
  }

  .kpi-trend {
    font-size: 13px;
    color: #6B7280;
    display: flex;
    align-items: center;
    gap: 4px;

    &.trend-up {
      color: #22C55E;
    }

    &.trend-down {
      color: #EF4444;
    }

    i {
      font-size: 14px;
    }
  }
}

.chart-section {
  margin-bottom: 16px;
}

.chart-card {
  border-radius: 8px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding-top: 12px;
  border-top: 1px solid #E5E7EB;
  margin-top: 12px;

  &--vertical {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    padding-left: 20px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #6B7280;
  }

  .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;

    &--green { background: #166534; }
    &--orange { background: #F59E0B; }
    &--red { background: #EF4444; }
    &--blue { background: #3B82F6; }
    &--purple { background: #8B5CF6; }
  }
}

.table-card {
  border-radius: 8px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }

  .table-actions {
    display: flex;
    gap: 12px;
  }
}

.supplier-info {
  .supplier-name {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
  }

  .supplier-contact {
    font-size: 12px;
    color: #9CA3AF;
    margin-top: 2px;
  }
}

.rate-value {
  font-weight: 600;
  font-size: 14px;

  &.rate-excellent { color: #166534; }
  &.rate-good { color: #F59E0B; }
  &.rate-poor { color: #EF4444; }
}

.status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  .status-icon {
    font-size: 16px;

    &--success { color: #22C55E; }
    &--warning { color: #F59E0B; }
    &--error { color: #EF4444; }
  }

  .status-text {
    font-size: 12px;

    &--success { color: #22C55E; }
    &--warning { color: #F59E0B; }
    &--error { color: #EF4444; }
  }
}

.delay-text--error {
  color: #EF4444;
  font-weight: 500;
}

.detail-btn {
  color: #3B82F6;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;

  .total-text {
    font-size: 14px;
    color: #6B7280;
  }
}
</style>
